import { Component, OnInit, ChangeDetectionStrategy, Input, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LatestTransactionRow } from '@kempoweroyj/ngx-evse-api-client';
import { NotificationsService } from 'api/notifications.api';
import { Transaction, TransactionsApiService } from '@kempoweroyj/ngx-transactions-api-client';
import { combineLatest, Subject } from 'rxjs';
import { auditTime, distinctUntilChanged, filter, startWith, switchMap, switchMapTo, take } from 'rxjs/operators';
import { isOccupied } from 'shared/connector-status-pipes/connector-status.pipe';
import { LocationsService } from 'shared/stations-service/locations.service';
import { StationsService } from 'shared/stations-service/stations.service';

export interface TransactionsByStationIds {
    [stationId: string]: {
        active: LatestTransactionRow[];
        recent: Transaction[];
    };
}

@Component({
    selector: 'ce-tile-sessions',
    templateUrl: './tile-sessions.component.html',
    styleUrls: ['./tile-sessions.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
@UntilDestroy()
export class TileSessionsComponent implements OnInit {
    @Input() stationId: string;
    @Input() locationId: string;

    transactionsByStationIds: TransactionsByStationIds = {};

    _stationIds: string[];

    activeTransactions: LatestTransactionRow[] = [];
    recentTransactions: Transaction[] = [];

    loading = true;

    activeSessions = $localize`:Session tile header|:active sessions`;
    sessions = $localize`:Session tile header|:Sessions`;

    private onDestroy$ = new Subject<void>();

    constructor(
        private notifications: NotificationsService,
        private txApi: TransactionsApiService,
        private cdRef: ChangeDetectorRef,
        private router: Router,
        private stationsService: StationsService,
        private locationsService: LocationsService
    ) {}

    ngOnInit(): void {
        // stationId is ignored if locationId is available
        if (this.locationId) {
            this.locationsService
                .locationStations$(this.locationId)
                .pipe(distinctUntilChanged(), untilDestroyed(this))
                .subscribe((ss) => {
                    // delete old station ids
                    this._stationIds = [];
                    this._stationIds = ss.map((s) => s.stationId);

                    this.cdRef.markForCheck();

                    this.getTransactionsByStationIds(this._stationIds);
                });
        } else if (this.stationId) {
            this._stationIds = [];
            this._stationIds = [this.stationId];

            this.cdRef.markForCheck();

            this.getTransactionsByStationIds(this._stationIds);
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    txTrackBy(idx: number, tx: Transaction | LatestTransactionRow) {
        return tx.txId;
    }

    getTransactionsByStationIds(ids: string[]) {
        // Subscribe to station
        this.notifications.connected$
            .pipe(
                filter((c) => c === true),
                untilDestroyed(this),
                take(1)
            )
            .subscribe(() => {
                this.notifications.subscribeUntil({ stationIds: ids }, this.onDestroy$);
            });

        for (const id of ids) {
            this.notifications.notification$
                .pipe(
                    filter(
                        (n) =>
                            n.type === 'ChargingStationUpdated' &&
                            n.stationId === id &&
                            n.changedSchemas?.includes('tx')
                    ),
                    auditTime(5000),
                    startWith([]),
                    switchMapTo(this.stationsService.seekTime$),
                    // fetch active transactions (through connectors), and also latest transactions
                    switchMap((seekTime) =>
                        combineLatest([
                            this.txApi.getTransactions(id, { limit: 20, endDate: seekTime || undefined }),
                            this.stationsService.connectors$(id),
                        ])
                    ),
                    untilDestroyed(this)
                )
                .subscribe(([res, connectors]) => {
                    // clear all old transactions
                    this.transactionsByStationIds[id] = { active: [], recent: [] };

                    this.transactionsByStationIds[id].active = connectors.filter((c) => isOccupied(c)).map((c) => c.tx);

                    // don't include active transactions in the recent ones list
                    this.transactionsByStationIds[id].recent = res.transactions.filter(
                        (tx) => !this.transactionsByStationIds[id]?.active.some((tx2) => tx2.txId === tx.txId)
                    );

                    const { allActive, allRecent } = mergeTransactionsInOneLocation(this.transactionsByStationIds);
                    this.activeTransactions = allActive;
                    this.recentTransactions = allRecent;

                    this.loading = false;
                    this.cdRef.markForCheck();
                });
        }
    }
}

function mergeTransactionsInOneLocation(blob: TransactionsByStationIds) {
    const _active: LatestTransactionRow[] = [];
    const _recent: Transaction[] = [];

    Object.values(blob).forEach((station) => {
        _active.push(...station.active);

        _recent.push(...station.recent);
    });

    // Sort by tx start time, desc
    const _activeSorted = _active.sort((a, b) => Date.parse(b?.startTime) - Date.parse(a?.startTime));
    const _recentSorted = _recent.sort((a, b) => Date.parse(b?.startTime) - Date.parse(a?.startTime));

    // Return
    return { allActive: _activeSorted, allRecent: _recentSorted };
}
